/**
 * Job matching engine — shared between Worker and UI
 * Ported from supabase/functions/_shared/jobMatching.ts
 */

import { isSourceEnabled } from '../constants/jobSources.js';

export function safeJson(input) {
  if (typeof input === 'object' && input !== null) return input;
  try {
    return typeof input === 'string' ? JSON.parse(input) : {};
  } catch {
    return {};
  }
}

export function parseSkills(input) {
  if (Array.isArray(input)) return input;
  const parsed = safeJson(input);
  return Array.isArray(parsed) ? parsed : [];
}

export function jobMatchesFilter(job, criteria) {
  if (!criteria) return false;

  // Per-source toggle. Backward compatible: a filter with no `sources` map
  // matches every origin (see isSourceEnabled). Upwork rows carry
  // source='upwork'; if absent we treat the job as Upwork.
  if (!isSourceEnabled(criteria, job && job.source)) return false;

  const useSearchIn = criteria.searchIn || { title: true, description: true, skills: true };
  const title = (job.title || '').toLowerCase();
  const description = (job.description || '').toLowerCase();
  const skillsArray = parseSkills(job.skills).map((s) => s.toLowerCase());
  const skillsText = skillsArray.join(' ');

  if (criteria.excludeKeywords && criteria.excludeKeywords.length > 0) {
    for (const keyword of criteria.excludeKeywords) {
      const k = keyword.toLowerCase();
      let found = false;
      if (useSearchIn.title && title.includes(k)) found = true;
      if (useSearchIn.description && description.includes(k)) found = true;
      if (useSearchIn.skills && skillsText.includes(k)) found = true;
      if (found) return false;
    }
  }

  if (criteria.keywords && criteria.keywords.length > 0) {
    let matchFound = false;
    for (const keyword of criteria.keywords) {
      const k = keyword.toLowerCase();
      if (useSearchIn.title && title.includes(k)) matchFound = true;
      if (useSearchIn.description && description.includes(k)) matchFound = true;
      if (useSearchIn.skills && skillsText.includes(k)) matchFound = true;
      if (matchFound) break;
    }
    if (!matchFound) return false;
  }

  const hasHourlyRate = job.min_hourly_rate != null;
  const hasFixedBudget = job.budget != null;

  if (criteria.onlyHourly && !hasHourlyRate) return false;
  if (criteria.onlyFixed && !hasFixedBudget) return false;
  if (criteria.hideNoBudget && !hasHourlyRate && !hasFixedBudget) return false;

  if (hasHourlyRate && criteria.hourlyRate) {
    if (criteria.hourlyRate.min && Number(job.min_hourly_rate) < Number(criteria.hourlyRate.min)) return false;
    if (criteria.hourlyRate.max && Number(job.min_hourly_rate) > Number(criteria.hourlyRate.max)) return false;
  }

  if (hasFixedBudget && criteria.fixedPrice) {
    if (criteria.fixedPrice.min && Number(job.budget) < Number(criteria.fixedPrice.min)) return false;
    if (criteria.fixedPrice.max && Number(job.budget) > Number(criteria.fixedPrice.max)) return false;
  }

  if (criteria.clientDetails) {
    const cd = criteria.clientDetails;
    const client = safeJson(job.client_data) || {};

    const isVerified = job.payment_verified === 1 || job.payment_verified === true;
    if (cd.paymentVerified && !isVerified) return false;

    const hires = Number(client.totalHires) || 0;
    let spend = 0;
    if (typeof client.totalSpent === 'object' && client.totalSpent?.rawValue) {
      spend = Number(client.totalSpent.rawValue) || 0;
    } else if (client.totalSpent) {
      spend = Number(client.totalSpent) || 0;
    }

    const rating = Number(client.totalFeedback) || 0;
    const reviews = Number(client.totalReviews) || 0;
    const hasHistory = hires > 0 && spend > 0;
    const shouldIncludeIfNoHistory = cd.includeNoHistory !== false;
    const skipStatsCheck = !hasHistory && shouldIncludeIfNoHistory;

    if (!skipStatsCheck) {
      if (cd.minSpend && spend < Number(cd.minSpend)) return false;
      if (cd.minHires && hires < Number(cd.minHires)) return false;
      if (cd.minRating && rating < Number(cd.minRating)) return false;
      if (cd.minReviews && reviews < Number(cd.minReviews)) return false;
    }

    const location = (job.country || client.country || '').toLowerCase();
    if (cd.preferredLocations && cd.preferredLocations.length > 0) {
      if (!cd.preferredLocations.some((pref) => location.includes(pref.toLowerCase()))) return false;
    }
    if (cd.avoidLocations && cd.avoidLocations.length > 0) {
      if (cd.avoidLocations.some((avoid) => location.includes(avoid.toLowerCase()))) return false;
    }
  }

  return true;
}
